/**
 * Credit customer-mail operator surface (router#2074).
 *
 * Three things this contract covers, and why they are one feature:
 *
 *   - a SWITCH that can stop the next pass without a deploy;
 *   - a per-account mute, copied from the balance-alert suppression;
 *   - an append-only delivery record that is NOT the idempotency gate.
 *
 * `credit_alert_deliveries` remains the gate: a row means "this key has
 * been handled, do not retry". The record table answers a different
 * question — who was addressed, which template, what subject, and whether
 * the attempt sent, suppressed, or failed. Failed retries append; they
 * do not overwrite.
 *
 * The switch SHIPS OPEN (seed `enabled = 1`) so the first promote does
 * not silence overdue letters that already mail. A missing row after
 * that seed is a fault, not a default: it reads as disabled, and the
 * pass posts to the ops Feishu chat. An unreadable switch is also
 * closed. Failed retries still append; a `mail_disabled` skip is
 * recorded at most once per delivery key.
 */

import type { EmailTemplateKey } from './internal-email-notices.ts';

export const CREDIT_CUSTOMER_MAIL_HISTORY_DEFAULT_LIMIT = 100;
export const CREDIT_CUSTOMER_MAIL_HISTORY_MAX_LIMIT = 500;

/**
 * How long an outbox row may sit unprocessed before the next pass
 * stamp-skips it as `stale` instead of sending. Measured against
 * the wall clock (the row's `created_at`), not the injected business
 * date the ladder uses for calendar facts.
 *
 * 48 hours is one weekend of operator pause on an hourly cron. It is
 * not the width of a due-soon stage: a 7-day letter whose calendar
 * fact has moved into the 3-day window is `superseded` even if the
 * row is younger than this. Stage mismatch and grant expiry are
 * separate checks.
 */
export const CREDIT_CUSTOMER_MAIL_FRESHNESS_MS = 48 * 60 * 60 * 1000;

export function creditCustomerTempExpiring3dDeliveryKey(grantId: string): string {
  return `customer:temp_expiring_3d:${grantId}`;
}

export function creditCustomerTempExpiring1dDeliveryKey(grantId: string): string {
  return `customer:temp_expiring_1d:${grantId}`;
}

export const CREDIT_CUSTOMER_MAIL_RESULTS = ['sent', 'suppressed', 'failed'] as const;
export type CreditCustomerMailResult = (typeof CREDIT_CUSTOMER_MAIL_RESULTS)[number];

export const CREDIT_CUSTOMER_MAIL_SUPPRESS_REASONS = [
  'unmapped_event',
  'no_recipient',
  'reserved_invalid_recipient',
  'missing_due_date',
  'zero_debt',
  'mail_disabled',
  'account_muted',
  'stale',
  'superseded',
] as const;
export type CreditCustomerMailSuppressReason =
  (typeof CREDIT_CUSTOMER_MAIL_SUPPRESS_REASONS)[number];

export const CREDIT_CUSTOMER_MAIL_FAIL_REASONS = [
  'provider_unavailable',
  'render_failed',
  'send_failed',
] as const;
export type CreditCustomerMailFailReason =
  (typeof CREDIT_CUSTOMER_MAIL_FAIL_REASONS)[number];

export type CreditCustomerMailReason =
  | CreditCustomerMailSuppressReason
  | CreditCustomerMailFailReason;

export function isCreditCustomerMailResult(value: string): value is CreditCustomerMailResult {
  for (const candidate of CREDIT_CUSTOMER_MAIL_RESULTS) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditCustomerMailSuppressReason(
  value: string,
): value is CreditCustomerMailSuppressReason {
  for (const candidate of CREDIT_CUSTOMER_MAIL_SUPPRESS_REASONS) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditCustomerMailFailReason(
  value: string,
): value is CreditCustomerMailFailReason {
  for (const candidate of CREDIT_CUSTOMER_MAIL_FAIL_REASONS) {
    if (candidate === value) return true;
  }
  return false;
}

/**
 * The global brake, as stored.
 *
 * The seed writes `enabled = 1`. Absence of the row is a fault: it
 * reads as disabled (`row_present = false`) so a stray DELETE cannot
 * reopen the channel.
 */
export interface CreditCustomerMailSettings {
  readonly enabled: boolean;
  readonly row_present: boolean;
  readonly reason: string | null;
  readonly updated_at: string | null;
  readonly updated_by_ops_user_id: string | null;
}

export interface CreditCustomerMailSettingsResponse {
  readonly settings: CreditCustomerMailSettings;
}

export interface UpdateCreditCustomerMailSettingsRequest {
  readonly enabled: boolean;
  readonly reason?: string | null;
}

export interface CreditCustomerMailSuppression {
  readonly account_id: string;
  readonly account_email: string | null;
  readonly reason: string | null;
  readonly created_at: string;
  readonly created_by_ops_user_id: string;
}

export interface CreditCustomerMailSuppressionsResponse {
  readonly suppressions: readonly CreditCustomerMailSuppression[];
}

export interface CreateCreditCustomerMailSuppressionRequest {
  readonly reason?: string | null;
}

export interface CreateCreditCustomerMailSuppressionResponse {
  readonly suppression: CreditCustomerMailSuppression;
}

export interface DeleteCreditCustomerMailSuppressionResponse {
  readonly removed: boolean;
}

/**
 * One processing attempt. Failed retries append a new row; they never
 * update an earlier one. Body text is not stored — subject, template key
 * and placeholder values are enough to reconstruct what was said without
 * keeping customer-amount prose around forever.
 */
export interface CreditCustomerMailDeliveryRecord {
  readonly id: string;
  readonly account_id: string;
  readonly event_id: string | null;
  readonly delivery_key: string;
  readonly to_address: string | null;
  readonly template_key: EmailTemplateKey | null;
  readonly subject: string | null;
  readonly placeholder_values: Readonly<Record<string, string>> | null;
  readonly result: CreditCustomerMailResult;
  readonly reason: CreditCustomerMailReason | null;
  readonly provider_message_id: string | null;
  readonly processed_at: string;
}

export interface CreditCustomerMailDeliveriesResponse {
  readonly entries: readonly CreditCustomerMailDeliveryRecord[];
}
